import { useState, useEffect, useRef } from 'react';
import { trackEvent } from '../utils/analytics';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { API_ENDPOINTS } from '../config/constants';

function Chatbot({ 
  programsData = {}, 
  getLocationPricing, 
  customHandler = null,
  initialMessages = null,
  quickPrompts = null,
  buttonLabel = 'Ask Chai',
  subtitle = 'Your Mentor to Lead With Tech',
  position = 'right', // 'right' or 'left'
  autoOpen = true
}) {
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [randomProgram, setRandomProgram] = useState(null);
  const [chatStep, setChatStep] = useState('initial'); // initial, collecting_name, collecting_email, collecting_mobile, completed
  const [chatLeadData, setChatLeadData] = useState({
    name: '',
    email: '',
    mobile: '',
    interestedIn: ''
  });
  const brandIconSrc = '/images/lwt.png';
  
  // Ref for auto-scrolling to bottom
  const messagesEndRef = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Initialize chatbot with random program on component mount
  useEffect(() => {
    // Use custom initial messages if provided
    if (initialMessages) {
      setChatMessages(initialMessages);
      if (autoOpen) {
        setTimeout(() => setShowChatbot(true), 1500);
      }
      return;
    }

    // Default behavior: random program suggestion
    const allPrograms = Object.values(programsData).filter(p => p.IsEnabled !== false);
    if (allPrograms.length === 0) return;
    
    const randomIndex = Math.floor(Math.random() * allPrograms.length);
    const selectedProgram = allPrograms[randomIndex];
    setRandomProgram(selectedProgram);
    
    // Set initial chatbot message
    const initialMessage = {
      type: 'bot',
      text: `Hi! I'm Chai, your Mentor to Lead With Tech!. 👋\n\nI noticed you might be interested in "${selectedProgram.title}". Would you like to know more about this ${selectedProgram.type.toLowerCase()}?`,
      timestamp: new Date().toISOString()
    };
    setChatMessages([initialMessage]);
    
    // Auto-open chatbot on non-mobile devices
    if (autoOpen) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      if (!isMobile) {
        // Delay opening to allow page to load
        setTimeout(() => setShowChatbot(true), 1500);
      }
    }
  }, [programsData, initialMessages, autoOpen]);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    
    // Add user message
    const userMessage = {
      type: 'user',
      text: chatInput,
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, userMessage]);
    
    const currentInput = chatInput;
    setChatInput('');

    // Use custom handler if provided
    if (customHandler) {
      customHandler(currentInput, chatMessages, setChatMessages, chatStep, setChatStep, chatLeadData, setChatLeadData);
      return;
    }
    
    // Handle lead collection flow
    if (chatStep === 'collecting_name') {
      setChatLeadData(prev => ({ ...prev, name: currentInput }));
      setChatStep('collecting_email');
      
      setTimeout(() => {
        const botMessage = {
          type: 'bot',
          text: `Great to meet you, ${currentInput}! 👋\n\nWhat's your email address so I can send you more details?`,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, botMessage]);
      }, 500);
      return;
    }
    
    if (chatStep === 'collecting_email') {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(currentInput)) {
        setTimeout(() => {
          const botMessage = {
            type: 'bot',
            text: `That doesn't look like a valid email address. Please enter a valid email (e.g., name@example.com)`,
            timestamp: new Date().toISOString()
          };
          setChatMessages(prev => [...prev, botMessage]);
        }, 500);
        return;
      }
      
      setChatLeadData(prev => ({ ...prev, email: currentInput }));
      setChatStep('collecting_mobile');
      
      setTimeout(() => {
        const botMessage = {
          type: 'bot',
          text: `Perfect! Last question - what's your mobile number?`,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, botMessage]);
      }, 500);
      return;
    }
    
    if (chatStep === 'collecting_mobile') {
      setChatLeadData(prev => ({ ...prev, mobile: currentInput }));
      setChatStep('submitting');
      
      // Submit lead to API
      const leadData = {
        name: chatLeadData.name,
        email: chatLeadData.email,
        mobile: currentInput,
        source: 'chatbot',
        interestedIn: randomProgram?.id || chatLeadData.interestedIn || 'general',
        consent: true,
        metadata: {
          programTitle: randomProgram?.title,
          chatTimestamp: new Date().toISOString()
        }
      };
      
      fetch(API_ENDPOINTS.LEADS_CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify(leadData)
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          trackEvent({
            event: 'lead_submit',
            category: 'Lead',
            label: 'chatbot'
          });
          
          setTimeout(() => {
            const botMessage = {
              type: 'bot',
              text: `🎉 Thank you! I've saved your information. Our team will reach out to you soon with more details about "${randomProgram?.title}".\n\nIs there anything else you'd like to know about our programs?`,
              timestamp: new Date().toISOString()
            };
            setChatMessages(prev => [...prev, botMessage]);
            setChatStep('completed');
          }, 500);
        } else {
          throw new Error(data.message || 'Failed to submit');
        }
      })
      .catch(error => {
        console.error('Lead submission error:', error);
        setTimeout(() => {
          const botMessage = {
            type: 'bot',
            text: `Sorry, there was an error saving your information. Please try again or contact us directly at our website.`,
            timestamp: new Date().toISOString()
          };
          setChatMessages(prev => [...prev, botMessage]);
          setChatStep('initial');
        }, 500);
      });
      
      setTimeout(() => {
        const botMessage = {
          type: 'bot',
          text: `⏳ Just a moment while I save your information...`,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, botMessage]);
      }, 300);
      return;
    }
    
    // Generate bot response based on keywords (normal conversation flow)
    setTimeout(() => {
      let botResponse = '';
      const input = currentInput.toLowerCase();
      
      if (input.includes('yes') || input.includes('sure') || input.includes('tell me') || input.includes('know more') || input.includes('interested')) {
        if (randomProgram && chatStep === 'initial') {
          const pricing = getLocationPricing ? getLocationPricing(randomProgram) : randomProgram.homePagePricing || { current: 'N/A', original: 'N/A' };
          botResponse = `Great! "${randomProgram.title}" is ${randomProgram.shortDescription}\n\n💰 Pricing: ${pricing.current} (originally ${pricing.original})\n\n⭐ Rating: ${randomProgram.rating.value}\n\nI'd love to send you more details. What's your name?`;
          setChatStep('collecting_name');
          setChatLeadData(prev => ({ ...prev, interestedIn: randomProgram.id }));
        } else if (randomProgram) {
          const pricing = getLocationPricing ? getLocationPricing(randomProgram) : randomProgram.homePagePricing || { current: 'N/A', original: 'N/A' };
          botResponse = `"${randomProgram.title}" is ${randomProgram.shortDescription}\n\n💰 Pricing: ${pricing.current} (originally ${pricing.original})\n\n⭐ Rating: ${randomProgram.rating.value}\n\nWould you like to explore more programs or get enrolled?`;
        }
      } else if (input.includes('price') || input.includes('cost') || input.includes('pricing')) {
        if (randomProgram) {
          const pricing = getLocationPricing ? getLocationPricing(randomProgram) : randomProgram.homePagePricing || { current: 'N/A', original: 'N/A' };
          botResponse = `The current price for "${randomProgram.title}" is ${pricing.current}, discounted from ${pricing.original}. Would you like me to send you more details?`;
        }
      } else if (input.includes('detail') || input.includes('explore') || input.includes('enroll') || input.includes('learn more') || input.includes('sign up')) {
        if (chatStep !== 'collecting_name' && chatStep !== 'collecting_email' && chatStep !== 'collecting_mobile') {
          botResponse = `I'd be happy to help you get started! First, may I have your name?`;
          setChatStep('collecting_name');
          if (randomProgram) {
            setChatLeadData(prev => ({ ...prev, interestedIn: randomProgram.id }));
          }
        }
      } else if (input.includes('more programs') || input.includes('other') || input.includes('different')) {
        const allPrograms = Object.values(programsData).filter(p => p.IsEnabled !== false);
        const programList = allPrograms.slice(0, 3).map(p => `• ${p.title}`).join('\n');
        botResponse = `Here are some of our popular programs:\n\n${programList}\n\nWhich one would you like to know more about?`;
      } else if (input.includes('hi') || input.includes('hello') || input.includes('hey')) {
        botResponse = `Hello! I'm here to help you find the perfect program. Are you interested in learning more about "${randomProgram?.title}"?`;
      } else if (chatStep === 'completed') {
        botResponse = `I can help you with:\n\n• Information about other programs\n• Pricing details\n• Learning paths and certifications\n\nWhat would you like to know?`;
      } else {
        botResponse = `I'd be happy to help! I can tell you about:\n\n• Program details and curriculum\n• Pricing and offers\n• How to enroll\n• Other available programs\n\nWhat would you like to know?`;
      }
      
      const botMessage = {
        type: 'bot',
        text: botResponse,
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, botMessage]);
    }, 500);
  };

  const handleQuickPrompt = (prompt) => {
    setChatMessages(prev => [...prev, { type: 'user', text: prompt, timestamp: new Date().toISOString() }]);
    const fakeEvent = { preventDefault: () => {}, target: { value: prompt } };
    setChatInput(prompt);
    setTimeout(() => handleChatSubmit(fakeEvent), 100);
  };

  const positionStyles = position === 'left' ? {
    bottom: '24px',
    left: showChatbot ? '24px' : '-400px',
  } : {
    bottom: '24px',
    right: showChatbot ? '24px' : '100px',
  };

  return (
    <div style={{
      position: 'fixed',
      ...positionStyles,
      zIndex: 1001,
      transition: 'all 0.3s ease'
    }}>
      {/* Chat Button */}
      {!showChatbot && (
        <button
          onClick={() => setShowChatbot(true)}
          style={{
            background: 'linear-gradient(135deg, #DA291C 0%, #b81f14 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '50px',
            padding: '16px 24px',
            fontSize: '1rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(218, 41, 28, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 25px rgba(218, 41, 28, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(218, 41, 28, 0.4)';
          }}
        >
          <img
            src={brandIconSrc}
            alt="Lead With Tech"
            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
          />
          {buttonLabel}
        </button>
      )}
      
      {/* Chat Window */}
      {showChatbot && (
        <div style={{
          width: '380px',
          height: '520px',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Chat Header */}
          <div style={{
            background: 'linear-gradient(135deg, #DA291C 0%, #b81f14 100%)',
            color: '#fff',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={brandIconSrc}
                alt="Lead With Tech"
                style={{ width: '36px', height: '36px', objectFit: 'contain' }}
              />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Ask Chai</h3>
                <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9 }}>{subtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setShowChatbot(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: '#fff',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>
          </div>

          {/* Quick Prompts */}
          {quickPrompts && quickPrompts.length > 0 && (
            <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px', background: '#fff' }}>
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt.value}
                  onClick={() => handleQuickPrompt(prompt.value)}
                  style={{
                    border: '1px solid #DA291C',
                    background: '#fff',
                    color: '#DA291C',
                    borderRadius: '20px',
                    padding: '6px 10px',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  {prompt.label}
                </button>
              ))}
            </div>
          )}
          
          {/* Chat Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            background: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {chatMessages.map((message, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                  animation: 'fadeIn 0.3s ease'
                }}
              >
                <div style={{
                  maxWidth: '75%',
                  padding: '12px 16px',
                  borderRadius: message.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: message.type === 'user' ? '#DA291C' : '#fff',
                  color: message.type === 'user' ? '#fff' : '#2c3e50',
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  whiteSpace: 'pre-line'
                }}>
                  {message.text}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Chat Input */}
          <form
            onSubmit={handleChatSubmit}
            style={{
              padding: '16px',
              background: '#fff',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              gap: '8px'
            }}
          >
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: '24px',
                border: '1px solid #ddd',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#DA291C'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
            />
            <button
              type="submit"
              style={{
                background: '#DA291C',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#b81f14';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#DA291C';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default Chatbot;
